import type { BaseObject, BaseProperty, RecordMode, TableFilterData } from './Base';
import type { Company, CompanySite } from './Companies';
import type { DeliveryType } from './DeliveryTypes';
import type { DocumentType } from './DocumentTypes';
import type { MaterialAlterCode } from './Materials';
import type { PaymentType } from './PaymentType';
import type { Customer, TraderSite } from './Traders';
import type { VatFactor } from './Vat';
import type { Warehouse } from './Warehouse';

export type DocumentHeader = {
  id: string;
  isFromManager: boolean;
  companyId?: string;
  company?: Company;
  companySiteId?: string;
  companySite?: CompanySite;
  documentTypeId?: string;
  documentType?: Partial<DocumentType>;
  documentSeriesId?: string;
  documentSeries?: Partial<BaseProperty>;
  divisionId?: string;
  division?: Partial<BaseObject>;
  documentNumber?: number;
  documentStatusId?: string;
  documentStatus?: Partial<BaseProperty>;
  officialDate?: string;
  deliveryDate?: string;
  printedDate?: string;
  approvesDate?: string;
  remarks?: string;
  isCanceled: boolean;
  reason?: string;
  headerRemarks?: string;
  footerRemarks?: string;
  notes?: string;
  canceledById?: string;
  issuedOnDeviceId?: string;
  qrBarCode?: string;
  signature?: string;
  totalGross: number;
  totalVat: number;
  totalCharges?: number;
  totalDiscounts: number;
  totalNetValue: number;
  totalQty: number;
  latitude?: string;
  longitude?: string;
  warehouse?: Warehouse;
  warehouseId?: string;
  documentPayment?: {
    id: string | null;
    paymentType?: PaymentType;
    startDate: string;
    endDate: string;
    paymentTypeId?: string;
    name?: string;
    deleted?: boolean;
  };
  mediator?: { id: string; code: string; name: string };
  mediatorId?: string | null;
  billingAddress?: TraderSite;
  billingAddressId?: string | null;
  deliveryAddress?: string;
  documentDetails?: DocumentDetail[];
  documentHeaderExpensesAndCharges?: DocumentHeaderExpensesAndCharges[];
  documentHeaderDiscounts?: DocumentHeaderDiscount[];
  traderId?: string;
  traderSiteId: string | undefined;
  traderSite?: { id: string; code: string; name: string; address?: string; boid?: string };
  myDataSyncSuccess?: boolean;
  myDataErrorMessage?: string;
  myDataMark?: number | null;
  myDataUuid?: string;
  myDataQrCode?: string;
};

export type DocumentDetail = {
  id: string;
  isFromManager: boolean;
  deleted?: boolean;
  documentHeaderId: string;
  warehouseId?: string;
  warehouse?: Warehouse;
  materialId: string;
  materialName: string;
  materialCode: string;
  material: { allowZeroValue?: boolean; allowManualPrice?: boolean; image?: string };
  materialAlterCodeId: string;
  materialAlterCode: Partial<MaterialAlterCode>;
  quantity: number;
  quantity2?: number;
  quantity3?: number;
  totalQuantity?: number;
  measurementUnit1Id: string;
  measurementUnit2Id?: string;
  measurementUnit3Id?: string;
  relationFactor1?: number;
  relationFactor2?: number;
  relationFactor3?: number;
  unitPrice: number;
  totalBeforeDiscount: number;
  unitDiscount: number;
  totalDiscount: number;
  percentageDiscount: number;
  vatFactorValue?: number;
  vatFactorId: string;
  vatFactor: VatFactor;
  netTotal: number;
  lineVatTotal: number;
  grossTotal: number;
  appliedDiscounts?: string | null;
  recordMode?: RecordMode;
  isModified?: boolean | null;
  documentDetailDiscounts?: DocumentDetailDiscount[];
  allowDiscount?: boolean;
  exists?: boolean;
  notExists?: boolean;
};

export type SalesDocument = {
  id: string;
  createdDate?: string;
  isActive: boolean;
  isFromManager: boolean;
  deleted: boolean;
  deliveryType?: Partial<DeliveryType>;
  deliveryTypeId?: string | null;
  customer?: Partial<Customer>;
  customerId?: string | null;
  salesPerson?: { id: string; code: string; name: string };
  salesPersonId?: string | null;
  documentHeader: DocumentHeader;
};

export type SalesDocumentForGrid = {
  id: string;
  createdDate: string;
  officialDate?: string;
  documentTypeName: string;
  documentSeriesName: string;
  documentStatusName: string;
  documentNumber: number;
  traderName?: string;
  salesPersonName?: string;
  totalGross: number;
  totalCharges: number;
  totalDiscounts: number;
  isActive: boolean;
  isFromManager: boolean;
};

export type SalesDocumentsFiltersData = TableFilterData & {
  dateFrom?: string;
  dateTo?: string;
  documentStatusIds?: string[];
  documentTypeIds?: string[];
  salesPersonId?: string | null;
  deleted?: boolean;
};

export type DocumentHeaderExpensesAndCharges = {
  id: string;
  isActive: boolean;
  isFromManager: boolean;
  grossValue: number;
  vatFactorValue?: number;
  vatValue?: number;
  value: number;
  expensesAndChargesId: string;
  expensesAndCharges: ExpensesAndCharges;
  recordMode?: RecordMode;
  extraDescription?: string;
  hasChanges?: boolean;
};

export type ExpensesAndCharges = {
  id: string;
  isActive: boolean;
  isFromManager: boolean;
  code: string;
  description: string;
  vatCategoryId?: string;
  vatCategory?: {
    id: string;
    code: string;
    name: string;
    boid: string;
    isActive: boolean;
    isFromManager: boolean;
  };
  allowValueChange: boolean;
  computedOnPrimaryValue: boolean;
  value: number;
  chargeValueType: eChargeValueType;
  chargeType: eChargesType;
};

export enum eChargeValueType {
  Percentage,
  Value,
}

export enum eChargesType {
  Charge,
  Expense,
  Tax,
}

export type DocumentHeaderDiscount = {
  id: string;
  isFromManager?: boolean;
  documentHeaderId: string;
  discountOrigin: eDiscountOrigin;
  sequence?: number;
  value: number;
  onGrossValue: boolean;
};

export type DocumentDetailDiscount = {
  id: string;
  documentDetailId: string;
  discountOrigin: eDiscountOrigin;
  discountUnitValue: number;
  discountPercentage: number;
  discountTotalValue: number;
  sequence: number;
};

export enum eDiscountOrigin {
  Line,
  Header,
  Custom,
}

export type MyDataDocumentHeader = {
  id: string;
  documentNumber: number;
  officialDate: string;
  totalGross: number;
  totalQuantity: number;
  myDataMark?: number;
  myDataSyncSuccess?: boolean;
  myDataErrorMessage?: string;
  myDataUuid?: string;
  myDataQrCode?: string;
  documentTypeName: string;
  divisionName: string;
  documentSeriesName: string;
  traderName: string;
  traderSiteName: string;
};

export type MyDataDocumentsFiltersData = TableFilterData & {
  dateFrom?: string;
  dateTo?: string;
  divisionIds?: string[];
  documentSeriesIds?: string[];
  documentTypeIds?: string[];
  syncedSuccess?: boolean;
  syncedFailure?: boolean;
  unsynced?: boolean;
};
